import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ErrorState, LoadingState } from '../global/components'
import { useAgents } from '../hooks/agents/useAgents'
import { useLanguage } from '../hooks/utility/useLanguage'
import { useAuth } from '../context/AuthContext'

const REDIRECT_TIMEOUT_MS = 10000 // 10 seconds

export const PrivilegedAgentRedirect = () => {
  const { agents, isLoadingAgents, agentError } = useAgents()
  const { refreshUser } = useAuth()
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [hasTimedOut, setHasTimedOut] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!isLoadingAgents && agents.length > 0) {
      // Clear timeout since we successfully loaded agents
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // Find the TIPP agent first (preferred default)
      const tippAgent = agents.find(agent => agent.slug === 'tipp' && agent.type === 'privileged')

      if (tippAgent) {
        // Redirect to the TIPP agent's page
        navigate(`/a/${tippAgent.id}`, { replace: true })
      } else {
        // Fallback: find any privileged agent
        const privilegedAgent = agents.find(agent => agent.type === 'privileged')

        if (privilegedAgent) {
          // Redirect to the privileged agent's page
          navigate(`/a/${privilegedAgent.id}`, { replace: true })
        } else {
          // Last fallback: redirect to the first available agent
          navigate(`/a/${agents[0].id}`, { replace: true })
        }
      }
    }
  }, [agents, isLoadingAgents, navigate])

  useEffect(() => {
    // Set up timeout for loading
    timeoutRef.current = setTimeout(() => {
      setHasTimedOut(true)
    }, REDIRECT_TIMEOUT_MS)

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    // Check if the error is a 401 unauthorized error and refresh the user token
    if (agentError && typeof agentError === 'string' && agentError.includes('401')) {
      refreshUser?.()
    }
  }, [agentError, refreshUser])

  const handleRetry = () => {
    setHasTimedOut(false)
    // Refresh user token on retry in case of auth issues
    refreshUser?.()
    // Reset timeout
    timeoutRef.current = setTimeout(() => {
      setHasTimedOut(true)
    }, REDIRECT_TIMEOUT_MS)
  }

  if (agentError || hasTimedOut) {
    return (
      <ErrorState
        message={agentError || t('errors.loadingTimeout')}
        minHeight="100vh"
        actionText={t('common.retry')}
        onAction={handleRetry}
      />
    )
  }

  return (
    <LoadingState
      message={t('common.loading')}
      fullHeight
      variant="spinner"
      size="lg"
      minHeight="100vh"
    />
  )
}
